'use client';

/**
 * useTheme — light / dark / system preference with system-preference
 * listening when the user has picked "system".
 *
 * Per theme T6 §12:
 *   Priority: user preference > default "light"
 *   "system" (follow prefers-color-scheme) applies only when a person stored it
 *   on purpose — the dark workspace is unfinished, see src/app/layout.tsx.
 *
 * The FOUC-prevention script in app/layout.tsx already applies the correct
 * theme before React hydrates, so this hook's job is state sync + runtime
 * switching — never first-paint.
 *
 * Usage:
 *   const { pref, resolved, setPref } = useTheme()
 *   <button onClick={() => setPref('dark')}>Dark</button>
 */
import { useCallback, useEffect, useState } from 'react';

import type { ResolvedTheme, ThemePreference } from './types';
import { STORAGE_KEY_THEME } from './types';

function readPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'light';
  try {
    const v = localStorage.getItem(STORAGE_KEY_THEME);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // localStorage blocked (private mode / some embedded contexts) — fine, default below.
  }
  return 'light';
}

function resolvePreference(pref: ThemePreference): ResolvedTheme {
  if (pref !== 'system') return pref;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyResolved(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
}

export function useTheme() {
  // SSR-safe defaults — corrected during the first effect.
  const [pref, setPrefState] = useState<ThemePreference>('system');
  const [resolved, setResolved] = useState<ResolvedTheme>('light');

  // Hydrate from localStorage + subscribe to system changes.
  useEffect(() => {
    const p = readPreference();
    setPrefState(p);
    const r = resolvePreference(p);
    setResolved(r);
    applyResolved(r);
  }, []);

  // When preference is "system", follow the OS / browser in real time.
  useEffect(() => {
    if (pref !== 'system') return;
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      const r = resolvePreference('system');
      setResolved(r);
      applyResolved(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const setPref = useCallback((next: ThemePreference): void => {
    try {
      localStorage.setItem(STORAGE_KEY_THEME, next);
    } catch {
      // ignore — same rationale as readPreference
    }
    setPrefState(next);
    const r = resolvePreference(next);
    setResolved(r);
    applyResolved(r);
  }, []);

  return { pref, resolved, setPref };
}
